import { ChangeEvent, useEffect, useState } from "react";
import { Autocomplete } from "@mui/material";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { changeServiceData, deleteServiceData, selectActiveRobotVersion } from "../../../store/ses/sesRobotSlice";
import { clearModel, getInfoModel, selectFullModel } from "../../../store/modelSlice";
import useAccessRight from "../../../hooks/useAccessRight";
import useTranslate from "../../../hooks/useTranslate";
import { SEE, SES, SMC } from "../../../constants/accessRights";
import { ROBOT_SERVICE_TYPE_LIST } from "../../../constants/robotConfigLists";
import { colorRed } from "../../../constants/colors";
import { ServiceTypeRobot } from "../../../types/cloudTypes";
import { RequestStatus } from "../../../types/statusTypes";
import ButtonIcon from "../../../ui/ButtonIcon/ButtonIcon";
import ProgressCircle from "../../../ui/ProgressCircle/ProgressCircle";
import { IServiceDataProps } from "./ServiceData.props";
import styles from "./ServiceData.module.scss";
import { CustomMenuItem, CustomTextField } from "@/ui";
import { RenderOption } from "@/ui";

const ServiceData = ({ data, serviceDataName, modelsList, changeFlgHandler }: IServiceDataProps): JSX.Element => {
	const [selectType, setSelectType] = useState<ServiceTypeRobot>(data.type); // тип
	const [inputModelName, setInputModelName] = useState<string>(""); // название модели
	const [inputClassname, setInputClassname] = useState<string>(""); // класс/сущность (param)
	const [classnamesList, setClassnamesList] = useState<string[]>([]); // список классов

	const dispatch = useAppDispatch();
	const fullModel = useAppSelector(selectFullModel); // store - модель
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за названием и сервисными данными и вписываем значения в поля
	useEffect(() => {
		setSelectType(data.type);
		setInputModelName(data.model);
		setInputClassname(data.param);
	}, [serviceDataName, data]);

	// следим за полем названия модели
	useEffect(() => {
		// через пол-секунды бездействия после окончания ввода названия модели
		const handler = setTimeout(() => {
			// если в списке моделей есть имя модели и есть тип
			if (modelsList.includes(inputModelName) && ((selectType === "smc" && isAccess(SMC.INFO)) || (selectType === "see" && isAccess(SEE.INFO)))) {
				dispatch(clearModel()); // очищаем данные модели
				dispatch(getInfoModel({ modelName: inputModelName, serviceType: selectType, modelNameAsIs: true })); // получаем инфо о модели
			}
		}, 500);

		return () => {
			clearTimeout(handler); // сбрасываем timeout, если продолжается ввод названия модели
		};
	}, [inputModelName]);

	// следим за данными модели
	useEffect(() => {
		// если в списке моделей есть имя модели
		if (modelsList.includes(inputModelName)) {
			// пишем классы модели в зависимости от выбранного типа
			setClassnamesList(fullModel.fullModel[inputModelName.includes("-new") ? "future" : inputModelName.includes("-backup") ? "previous" : "current"]?.classes || []);
		}
	}, [fullModel.fullModel]);

	// обработчик изменения типа
	const changeTypeHandler = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		const value = e.target.value as ServiceTypeRobot;
		dispatch(changeServiceData({ serviceDataName, data: { field: "type", value } }));
		changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
	};

	// обработчик изменения модели
	const changeModelHandler = (): void => {
		if (data.model !== inputModelName) {
			dispatch(changeServiceData({ serviceDataName, data: { field: "model", value: inputModelName } }));
		}
	};

	// обработчик изменения класса/сущности
	const changeParamHandler = (): void => {
		if (data.param !== inputClassname) {
			dispatch(changeServiceData({ serviceDataName, data: { field: "param", value: inputClassname } }));
		}
	};

	// обработчик удаления сервисных данных
	const deleteServiceDataHandler = () => {
		dispatch(deleteServiceData({ serviceDataName }));
		changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
	};

	return (
		<div className={styles.container}>
			<div className={styles.block}>
				{/* тип */}
				<CustomTextField
					select
					fullWidth
					required
					label={translate("select_type")}
					value={selectType}
					onChange={changeTypeHandler}
					disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
				>
					{ROBOT_SERVICE_TYPE_LIST.map(({ type, translation }) => (
						<CustomMenuItem key={type} value={type}>
							{translate(translation)}
						</CustomMenuItem>
					))}
				</CustomTextField>

				{/* удаление сервисных данных */}
				{isAccess(SES.ROBOT_EDIT) && activeRobotVersion === "draft" && (
					<ButtonIcon icon={faTrashCan} iconColor={colorRed} iconSize="1x" title={translate("button_delete")} onClick={deleteServiceDataHandler} />
				)}
			</div>

			{/* модель */}
			<Autocomplete
				fullWidth
				freeSolo
				options={modelsList}
				value={inputModelName}
				onChange={(_, value) => {
					setInputModelName(value ? value : "");
					changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
				}}
				onBlur={changeModelHandler}
				disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
				renderInput={(params) => (
					<CustomTextField
						{...params}
						required
						label={translate("input_model")}
						onChange={(e) => {
							setInputModelName(e.target.value);
							changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
						}}
						error={!inputModelName}
						// InputProps={{
						// 	...params.InputProps, // важно прокинуть параметры
						// 	endAdornment: (
						// 		<>
						// 			{clusterServer.status === RequestStatus.LOADING && <ProgressCircle isBtnDisabled /> }
						// 			{params.InputProps.endAdornment} {/* важно дописать параметры */}
						// 		</>
						// 	),
						// }}
					/>
				)}
				renderOption={(props, option) => {
					return (
						<RenderOption {...props} key={option}>
							{option}
						</RenderOption>
					);
				}}
			/>

			{/* класс/сущность */}
			<Autocomplete
				fullWidth
				freeSolo
				options={classnamesList}
				value={inputClassname}
				onChange={(_, value) => {
					setInputClassname(value ? value : "");
					changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
				}}
				onBlur={changeParamHandler}
				disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
				renderInput={(params) => (
					<CustomTextField
						{...params}
						required
						label={translate(selectType === "smc" ? "input_class" : "input_entity")}
						onChange={(e) => {
							setInputClassname(e.target.value);
							changeFlgHandler("servicedata"); // ставим флаг о несохраненных данных
						}}
						error={!inputClassname}
						InputProps={{
							...params.InputProps, // важно прокинуть параметры
							endAdornment: (
								<>
									{fullModel.status === RequestStatus.LOADING && <ProgressCircle isBtnDisabled />}
									{params.InputProps.endAdornment} {/* важно дописать параметры */}
								</>
							),
						}}
					/>
				)}
				renderOption={(props, option) => {
					return (
						<RenderOption {...props} key={option}>
							{option}
						</RenderOption>
					);
				}}
			/>
		</div>
	);
};

export default ServiceData;
